// @mui material components
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import Card from "@mui/material/Card";
import { Link } from "react-router-dom";
import { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";

// Soft UI Dashboard PRO React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";
import SalesTable from "examples/Tables/SalesTable";
import ReportsBarChart from "examples/Charts/BarCharts/ReportsBarChart";
import GradientLineChart from "examples/Charts/LineCharts/GradientLineChart";
import Globe from "examples/Globe";

// Soft UI Dashboard PRO React base styles
import typography from "assets/theme/base/typography";
import breakpoints from "assets/theme/base/breakpoints";

// Data
import salesTableData from "layouts/dashboards/default/data/salesTableData";
import reportsBarChartData from "layouts/dashboards/default/data/reportsBarChartData";
import gradientLineChartData from "layouts/dashboards/default/data/gradientLineChartData";

// Dashboard API service
import { getDashboardData } from "services/dashboardService";
import { AuthContext } from "context";

const buildDefaultPortalSections = (settings) => [
  {
    title: "SBOffice",
    description: "Verwalten Sie Ihre SBOffice-Lizenz, Mandantendaten und digitalen Arbeitsplaetze.",
    icon: "business",
    route: "/services/sboffice",
    action_label: "SBOffice oeffnen",
  },
  {
    title: "Ihre IaaS-Services",
    description: "Hier finden Sie Ihre Server, Cloud-Ressourcen und Betriebsinformationen.",
    icon: "cloud",
    route: "/iaas/server",
    action_label: "IaaS oeffnen",
  },
  {
    title: "Support",
    description: `${settings?.support_email || "consulting@uds-solutions.de"} | ${settings?.support_phone || "+49 (0) 4961 7782753"}`,
    icon: "support_agent",
    route: "/Support",
    action_label: "Support oeffnen",
  },
];

function PortalInfoDashboard({ settings }) {
  const sections = Array.isArray(settings?.portal_home_sections) && settings.portal_home_sections.length > 0
    ? settings.portal_home_sections
    : buildDefaultPortalSections(settings);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <Card sx={{ mb: 3 }}>
          <SoftBox p={{ xs: 3, md: 4 }}>
            <SoftTypography variant="h3" fontWeight="bold" mb={1}>
              {settings?.portal_home_title || "Willkommen im Schloermann Portal"}
            </SoftTypography>
            <SoftTypography variant="body2" color="text" maxWidth="760px">
              {settings?.portal_home_intro ||
                "Hier finden Sie Informationen zu Ihren IaaS-Services, SBOffice, Dokumentation und Support."}
            </SoftTypography>
          </SoftBox>
        </Card>

        <Grid container spacing={3}>
          {sections.map((section, index) => {
            const route = section.route || null;
            const isInternalRoute = route && route.startsWith("/");

            return (
              <Grid item xs={12} md={4} key={`${section.title || "section"}-${index}`}>
                <Card sx={{ height: "100%" }}>
                  <SoftBox p={3} height="100%" display="flex" flexDirection="column">
                    <SoftBox
                      display="grid"
                      justifyContent="center"
                      alignItems="center"
                      bgColor="info"
                      color="white"
                      width="3rem"
                      height="3rem"
                      shadow="md"
                      borderRadius="lg"
                      mb={2}
                    >
                      <Icon fontSize="default">{section.icon || "info"}</Icon>
                    </SoftBox>
                    <SoftTypography variant="h5" fontWeight="bold" mb={1}>
                      {section.title || "Information"}
                    </SoftTypography>
                    <SoftTypography variant="body2" color="text" mb={3}>
                      {section.description || ""}
                    </SoftTypography>
                    {route && (
                      <SoftBox mt="auto">
                        {isInternalRoute ? (
                          <SoftButton component={Link} to={route} variant="gradient" color="info" size="small">
                            {section.action_label || section.actionLabel || "Oeffnen"}
                          </SoftButton>
                        ) : (
                          <SoftButton component="a" href={route} variant="gradient" color="info" size="small">
                            {section.action_label || section.actionLabel || "Oeffnen"}
                          </SoftButton>
                        )}
                      </SoftBox>
                    )}
                  </SoftBox>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </SoftBox>
      <Footer />
    </DashboardLayout>
  );
}

PortalInfoDashboard.propTypes = {
  settings: PropTypes.shape({
    support_email: PropTypes.string,
    support_phone: PropTypes.string,
    portal_home_title: PropTypes.string,
    portal_home_intro: PropTypes.string,
    portal_home_sections: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
        description: PropTypes.string,
        icon: PropTypes.string,
        route: PropTypes.string,
        action_label: PropTypes.string,
        actionLabel: PropTypes.string,
      })
    ),
  }),
};

PortalInfoDashboard.defaultProps = {
  settings: null,
};

function Default() {
  const { values } = breakpoints;
  const { size } = typography;
  const { chart, items } = reportsBarChartData;
  const { activePortalSettings, isRestrictedPortal } = useContext(AuthContext);

  // State for dashboard data
  const [dashboardData, setDashboardData] = useState({
    activeIaasProducts: [],
    activeSaasProducts: [],
    openTickets: [],
    serverAvailability: {
      totalServers: 0,
      onlineServers: 0,
      offlineServers: 0,
      warningServers: 0,
      overallUptime: 100
    },
    activeServers: []
  });
  const [loading, setLoading] = useState(true);

  // Fetch dashboard data on component mount
  useEffect(() => {
    const fetchData = async () => {
      if (isRestrictedPortal) {
        setLoading(false);
        return;
      }

      try {
        const data = await getDashboardData();
        setDashboardData(data);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isRestrictedPortal]);

  if (isRestrictedPortal) {
    return <PortalInfoDashboard settings={activePortalSettings} />;
  }

  // Format server availability text and color
  const getServerAvailabilityInfo = () => {
    // Default values if serverAvailability is null or undefined
    const availability = dashboardData.serverAvailability || {
      overallUptime: 100,
      onlineServers: 0,
      offlineServers: 0,
      warningServers: 0,
      totalServers: 0
    };
    
    const { overallUptime, onlineServers, offlineServers, warningServers, totalServers } = availability;
    
    if (offlineServers === 0 && warningServers === 0) {
      return {
        color: "success", 
        text: "Keine Störung"
      };
    } else if (warningServers > 0 && offlineServers === 0) {
      return {
        color: "warning", 
        text: `${warningServers} Services mit Warnungen`
      };
    } else if (offlineServers > 0) {
      return {
        color: "error", 
        text: `${offlineServers} Services offline`
      };
    } else {
      return {
        color: "success", 
        text: "Keine Störung"
      };
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <Grid container>
          <Grid item xs={12} lg={7}>
            <SoftBox mb={3} p={1}>
              <SoftTypography
                variant={window.innerWidth < values.sm ? "h3" : "h2"}
                textTransform="capitalize"
                fontWeight="bold"
              >
                Willkommen im Kundenportal
              </SoftTypography>
            </SoftBox>

            <Grid container>
              <Grid item xs={12}>
                <Globe
                  display={{ xs: "none", md: "block" }}
                  position="absolute"
                  top="10%"
                  right={0}
                  mt={{ xs: -12, lg: 1 }}
                  mr={{ xs: 0, lg: 10 }}
                  canvasStyle={{ marginTop: "3rem" }}
                />
              </Grid>
            </Grid>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={5}>
                <SoftBox mb={3}>
                  <MiniStatisticsCard
                    title={{ text: "aktive IaaS-Produkte", fontWeight: "bold" }}
                    count={loading ? "..." : (dashboardData.activeIaasProducts?.length || 0).toString()}
                    percentage={{ 
                      color: dashboardData.activeIaasProducts?.length > 0 ? "success" : "warning", 
                      text: loading ? "Wird geladen..." : 
                        (dashboardData.activeIaasProducts?.length > 0 
                          ? "Systeme"
                          : "keine aktiven Produkte")
                    }}
                    icon={{ color: "info", component: "cloud" }}
                  />
                </SoftBox>
                <MiniStatisticsCard
                  title={{ text: "aktive SaaS-Produkte", fontWeight: "bold" }}
                  count={loading ? "..." : (dashboardData.activeSaasProducts?.length || 0).toString()}
                  percentage={{ 
                    color: dashboardData.activeSaasProducts?.length > 0 ? "success" : "warning", 
                    text: loading ? "Wird geladen..." : 
                      (dashboardData.activeSaasProducts?.length > 0 
                        ? "Produkte in Wartung"
                        : "keine aktiven Produkte")
                  }}
                  icon={{ color: "info", component: "subscriptions" }}
                />
              </Grid>
              <Grid item xs={12} sm={5}>
                <SoftBox mb={3}>
                  <MiniStatisticsCard
                    title={{ text: "Server-Verfügbarkeit", fontWeight: "bold" }}
                    count={loading ? "..." : `${dashboardData.serverAvailability.overallUptime || 100} %`}
                    percentage={{ 
                      color: loading ? "info" : getServerAvailabilityInfo().color,
                      text: loading ? "Wird geladen..." : getServerAvailabilityInfo().text
                    }}
                    icon={{ color: "info", component: "check_circle" }}
                  />
                </SoftBox>
                <SoftBox mb={3}>
                  <MiniStatisticsCard
                    title={{ text: "Offene Tickets", fontWeight: "bold" }}
                    count={loading ? "..." : (dashboardData.openTickets?.length || 0).toString()}
                    percentage={{ 
                      color: dashboardData.openTickets?.length > 0 ? "warning" : "success", 
                      text: loading ? "Wird geladen..." : 
                        (dashboardData.openTickets?.length > 0 
                          ? "Tickets benötigen Aufmerksamkeit" 
                          : "keine offenen Tickets")
                    }}
                    icon={{
                      color: "info",
                      component: "data_usage",
                    }}
                  />
                </SoftBox>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} md={10} lg={7}>
            <SoftBox
              mb={3}
              position="relative"
              p={3}
              borderRadius="lg"
              boxShadow="lg"
              bgcolor="white"
              width="83%"
              style={{ backgroundColor: "#ffffff" }} // Sicherstellen, dass der Hintergrund nicht durchsichtig ist
            >
              <SoftTypography variant="h5" fontWeight="bold" mb={2}>
                Aktuelle Angebote
              </SoftTypography>
              <SoftBox component="ul" pl={2} m={0}>
                <SoftBox display="flex" alignItems="center" mb={2}>
                  <Icon sx={{ color: "info", mr: 1 }}>business</Icon>
                  <SoftTypography component="span" variant="body2" color="text">
                    <strong>SBOffice:</strong> Digitale Arbeitsplaetze und Mandanten zentral verwalten
                  </SoftTypography>
                </SoftBox>
                <SoftBox display="flex" alignItems="center" mb={2}>
                  <Icon sx={{ color: "success", mr: 1 }}>folder_shared</Icon>
                  <SoftTypography component="span" variant="body2" color="text">
                    <strong>UDS ECM:</strong> Dokumente, Workflows und Informationen strukturiert abbilden
                  </SoftTypography>
                </SoftBox>
                <SoftBox display="flex" alignItems="center">
                  <Icon sx={{ color: "warning", mr: 1 }}>business</Icon>
                  <SoftTypography component="span" variant="body2" color="text">
                    <strong>UDS Private Cloud:</strong> Maximale Kontrolle und Sicherheit für Unternehmen
                  </SoftTypography>
                </SoftBox>
              </SoftBox>
            </SoftBox>
          </Grid>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <SoftBox
                mb={3}
                position="relative"
                p={3}
                borderRadius="lg"
                boxShadow="lg"
                bgcolor="white"
                width="100%"
                style={{ backgroundColor: "#ffffff" }}
              >
                <SoftTypography variant="h5" fontWeight="bold" mb={2}>
                  Produkte entdecken
                </SoftTypography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <SoftTypography variant="h6" fontWeight="bold" mb={2}>
                      Software & Plattform
                    </SoftTypography>
                    <SoftBox component="ul" pl={2} m={0}>
                      <SoftBox display="flex" alignItems="center" mb={2}>
                        <Icon sx={{ color: "info", mr: 1 }}>business</Icon>
                        <SoftTypography component="span" variant="body2" color="text">
                          <strong>SBOffice:</strong> Zentrale Verwaltung fuer digitale Arbeitsplaetze
                        </SoftTypography>
                      </SoftBox>
                      <SoftBox display="flex" alignItems="center" mb={2}>
                        <Icon sx={{ color: "success", mr: 1 }}>folder_shared</Icon>
                        <SoftTypography component="span" variant="body2" color="text">
                          <strong>UDS ECM:</strong> Dokumente und Prozesse nachvollziehbar organisieren
                        </SoftTypography>
                      </SoftBox>
                      <SoftBox display="flex" alignItems="center" mb={2}>
                        <Icon sx={{ color: "secondary", mr: 1 }}>description</Icon>
                        <SoftTypography component="span" variant="body2" color="text">
                          <strong>MS Office 365:</strong> Die volle Power von Microsoft Office 365
                        </SoftTypography>
                      </SoftBox>
                      <SoftBox display="flex" alignItems="center" mb={2}>
                        <Icon sx={{ color: "warning", mr: 1 }}>security</Icon>
                        <SoftTypography component="span" variant="body2" color="text">
                          <strong>ESET:</strong> Schützen Sie Ihre Geräte mit Sicherheitslösungen
                        </SoftTypography>
                      </SoftBox>
                    </SoftBox>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <SoftTypography variant="h6" fontWeight="bold" mb={2}>
                      IaaS-Produkte
                    </SoftTypography>
                    <SoftBox component="ul" pl={2} m={0}>
                      <SoftBox display="flex" alignItems="center" mb={2}>
                        <Icon sx={{ color: "success", mr: 1 }}>computer</Icon>
                        <SoftTypography component="span" variant="body2" color="text">
                          <strong>Windows/Linux Cloud Server:</strong> Leistungsstarke Server in der Cloud
                        </SoftTypography>
                      </SoftBox>
                      <SoftBox display="flex" alignItems="center" mb={2}>
                        <Icon sx={{ color: "error", mr: 1 }}>storage</Icon>
                        <SoftTypography component="span" variant="body2" color="text">
                          <strong>S3/Cloud Storage:</strong> Skalierbarer Speicherplatz für Ihre Daten
                        </SoftTypography>
                      </SoftBox>
                      <SoftBox display="flex" alignItems="center" mb={2}>
                        <Icon sx={{ color: "primary", mr: 1 }}>phone</Icon>
                        <SoftTypography component="span" variant="body2" color="text">
                          <strong>PBX Cloud:</strong> Professionelle Telefonanlage in der Cloud
                        </SoftTypography>
                      </SoftBox>
                      <SoftBox display="flex" alignItems="center" mb={2}>
                        <Icon sx={{ color: "secondary", mr: 1 }}>cloud_done</Icon>
                        <SoftTypography component="span" variant="body2" color="text">
                          <strong>Private/Hybrid Cloud:</strong> Maximale Kontrolle und Sicherheit
                        </SoftTypography>
                      </SoftBox>
                    </SoftBox>
                  </Grid>
                </Grid>
              </SoftBox>
            </Grid>
          </Grid>
        </Grid>
      </SoftBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Default;
